import React, { useEffect, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { Card } from '@/components/Card';
import { Button } from '@/components/Button';
import { articleAdminApi } from '@/features/admin/articleAdminApi';
import type { TopViewedArticleEntry } from '@/features/admin/articleAdminApi';

type GroupType = 'category' | 'topic';

export const ArticleTopViews: React.FC = () => {
  const [searchParams] = useSearchParams();

  const groupTypeRaw = searchParams.get('groupType');
  const groupType: GroupType | null =
    groupTypeRaw === 'category' || groupTypeRaw === 'topic' ? groupTypeRaw : null;
  const code = searchParams.get('code') || '';
  const country = searchParams.get('country') || undefined;
  const label = searchParams.get('label') || code;

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [items, setItems] = useState<TopViewedArticleEntry[]>([]);

  useEffect(() => {
    if (!groupType || !code) {
      setItems([]);
      setError(null);
      return;
    }

    setLoading(true);
    setError(null);

    articleAdminApi
      .getTopViewedArticles({
        groupType,
        code,
        country,
        limit: 20,
      })
      .then(data => {
        setItems(data);
      })
      .catch(err => {
        const message = err instanceof Error ? err.message : 'Failed to load top viewed articles';
        setItems([]);
        setError(message);
      })
      .finally(() => {
        setLoading(false);
      });
  }, [groupType, code, country]);

  return (
    <div className="container mx-auto px-4 py-8">
      <div className="mb-8 rounded-3xl border border-gray-200 bg-gradient-to-r from-slate-50 via-white to-amber-50 p-6 shadow-sm">
        <div className="flex flex-col gap-4 md:flex-row md:items-end md:justify-between">
          <div>
            <h1 className="text-3xl font-bold text-gray-900">Top 20 articles by views</h1>
            <p className="mt-2 text-gray-600">
              {groupType && code
                ? `Showing ${label}${country ? ` (${country})` : ''}`
                : 'Choose a category/topic from Article statistics'}
            </p>
          </div>
          <Link to="/admin/article-statistics">
            <Button variant="outline">Back to Article Statistics</Button>
          </Link>
        </div>
      </div>

      <Card className="p-6">
        {!groupType || !code ? (
          <p className="text-sm text-gray-500">Missing query params. Open this page from totals.</p>
        ) : null}
        {loading ? <p className="text-sm text-gray-500">Loading top articles...</p> : null}
        {!loading && error ? <p className="text-sm text-red-700">{error}</p> : null}
        {!loading && !error && groupType && code && items.length === 0 ? (
          <p className="text-sm text-gray-500">No articles found.</p>
        ) : null}

        {!loading && !error && items.length > 0 ? (
          <div className="overflow-x-auto">
            <table className="w-full min-w-[760px]">
              <thead>
                <tr className="border-b border-gray-200 text-left text-xs uppercase tracking-wide text-gray-500">
                  <th className="px-2 py-3">#</th>
                  <th className="px-2 py-3">Article</th>
                  <th className="px-2 py-3">Category</th>
                  <th className="px-2 py-3">Published</th>
                  <th className="px-2 py-3">Views</th>
                </tr>
              </thead>
              <tbody>
                {items.map((item, index) => (
                  <tr key={item.articleId} className="border-b border-gray-100 text-sm">
                    <td className="px-2 py-3 font-semibold text-gray-700">{index + 1}</td>
                    <td className="px-2 py-3">
                      <p className="font-medium text-gray-900">{item.title}</p>
                      <p className="text-xs text-gray-500">{item.articleId}</p>
                    </td>
                    <td className="px-2 py-3 text-gray-700">{item.category}</td>
                    <td className="px-2 py-3 text-gray-700">{item.publishedAt || '-'}</td>
                    <td className="px-2 py-3 font-semibold text-blue-700">
                      {item.viewCount.toLocaleString()}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ) : null}
      </Card>
    </div>
  );
};
